import React from "react";
import { cn } from "@/lib/utils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";

interface RiskProfile {
  id: string;
  label: string;
  color: string;
  volatility: number; // 0-1, how much the line fluctuates
  expectedReturn: number; // annual return percentage
}

interface RiskProfileChartProps {
  selectedProfile?: string;
  profiles?: RiskProfile[];
  years?: number;
  animated?: boolean;
  className?: string;
}

const DEFAULT_PROFILES: RiskProfile[] = [
  { id: "konservativ", label: "Konservativ", color: "hsl(142, 76%, 36%)", volatility: 0.05, expectedReturn: 0.02 },
  { id: "ausgewogen", label: "Ausgewogen", color: "hsl(45, 93%, 47%)", volatility: 0.12, expectedReturn: 0.05 },
  { id: "dynamisch", label: "Dynamisch", color: "hsl(25, 95%, 53%)", volatility: 0.2, expectedReturn: 0.08 },
  { id: "offensiv", label: "Offensiv", color: "hsl(0, 84%, 60%)", volatility: 0.3, expectedReturn: 0.12 },
];

export function RiskProfileChart({
  selectedProfile = "ausgewogen",
  profiles = DEFAULT_PROFILES,
  years = 10,
  animated = true,
  className,
}: RiskProfileChartProps) {
  // Generate simulated chart data
  const generateData = (profile: RiskProfile) => {
    const data = [];
    let value = 100;
    
    for (let month = 0; month <= years * 12; month++) {
      // Add some randomized volatility
      const randomFactor = 1 + (Math.random() - 0.5) * profile.volatility * 2;
      const growthFactor = 1 + profile.expectedReturn / 12;
      value = value * growthFactor * randomFactor;
      
      if (month % 12 === 0) {
        data.push({
          year: month / 12,
          value: Math.round(value * 10) / 10,
        });
      }
    }
    return data;
  };

  const currentProfile = profiles.find(p => p.id === selectedProfile) || profiles[1];
  const chartData = React.useMemo(() => generateData(currentProfile), [currentProfile]);

  return (
    <div className={cn("space-y-6", className)}>
      <div className="text-center">
        <h3 className="font-semibold text-lg">Risikoprofil-Simulation</h3>
        <p className="text-sm text-muted-foreground">
          Erwartete Wertentwicklung über {years} Jahre
        </p>
      </div>

      {/* Risk Profile Selector */}
      <div className="flex justify-center gap-2">
      {profiles.map((profile) => (
          <div
            key={profile.id}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium transition-all",
              profile.id === selectedProfile
                ? "ring-2 ring-offset-2 ring-current"
                : "opacity-50"
            )}
            style={{
              backgroundColor: profile.color + "20",
              color: profile.color,
            }}
          >
            {profile.label}
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={currentProfile.color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={currentProfile.color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="year"
              tickFormatter={(value) => `Jahr ${value}`}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={(value) => `${value}%`}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              domain={['dataMin - 10', 'dataMax + 10']}
            />
            <Tooltip
              formatter={(value: number) => [`${value.toFixed(1)}%`, 'Wert']}
              labelFormatter={(label) => `Jahr ${label}`}
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={currentProfile.color}
              strokeWidth={2}
              fill="url(#colorValue)"
              animationDuration={animated ? 1500 : 0}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Profile Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-3 bg-muted/50 rounded-lg text-center">
          <span className="block text-xs text-muted-foreground">Erwartete Rendite p.a.</span>
          <span className="text-lg font-semibold" style={{ color: currentProfile.color }}>
            {(currentProfile.expectedReturn * 100).toFixed(1)}%
          </span>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg text-center">
          <span className="block text-xs text-muted-foreground">Risiko/Volatilität</span>
          <span className="text-lg font-semibold" style={{ color: currentProfile.color }}>
            {currentProfile.volatility < 0.1 ? "Niedrig" : 
             currentProfile.volatility < 0.2 ? "Mittel" : 
             currentProfile.volatility < 0.25 ? "Hoch" : "Sehr hoch"}
          </span>
        </div>
      </div>
    </div>
  );
}
